import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown } from 'lucide-react';

/**
 * One Copy button instead of one per format.
 *
 * Four formats as four buttons ate the footer and put the rarely-used ones
 * (Markdown, HTML) at the same weight as the ones people actually reach for.
 *
 * Rendered through a portal because the footer is inside an overflow-hidden
 * grid pane — a menu positioned normally would be clipped by it.
 */

export const COPY_FORMATS = [
  { id: 'csv', label: 'CSV' },
  { id: 'json', label: 'JSON' },
  { id: 'md', label: 'Markdown' },
  { id: 'html', label: 'HTML table' },
];

export default function DbCopyMenu({ onCopy, copiedFormat, disabled, label = 'Copy' }) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      // A click on the trigger itself is the toggle; let its own handler win.
      if (buttonRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    // Scrolling would leave the menu behind, since it is positioned once.
    window.addEventListener('resize', () => setOpen(false), { once: true });
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = () => {
    if (disabled) return;
    if (open) {
      setOpen(false);
      return;
    }
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      // Above the button: this lives in a footer, so downward would go
      // off-screen.
      setAnchor({ left: rect.left, bottom: window.innerHeight - rect.top + 4 });
    }
    setOpen(true);
  };

  // The flash after copying replaces the label, so the feedback lands on the
  // control the user actually pressed.
  const copiedLabel = COPY_FORMATS.find((f) => f.id === copiedFormat)?.label;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="dbm-pager-btn dbm-copy-trigger"
        onClick={toggle}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {copiedLabel ? `Copied ${copiedLabel}` : label}
        <ChevronDown size={12} aria-hidden />
      </button>
      {open && anchor && ReactDOM.createPortal(
        <div
          className="dyn-var-suggestions dbm-copy-menu"
          style={{ left: anchor.left, bottom: anchor.bottom, minWidth: 150, maxHeight: 'none' }}
          role="menu"
        >
          {COPY_FORMATS.map((format) => (
            <div
              key={format.id}
              role="menuitem"
              tabIndex={0}
              className="dyn-var-suggestion-item"
              onMouseDown={(e) => {
                e.preventDefault();
                setOpen(false);
                onCopy(format.id);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setOpen(false);
                  onCopy(format.id);
                }
              }}
            >
              <span className="dyn-var-suggestion-name">{format.label}</span>
            </div>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}
